/* ==========================================================================
   365 Flips — Cart drawer "1b" behaviour
   - "Added to cart" state (header + badge) when the drawer opens after an add
   - Pill quantity stepper wired into Dawn's cart-drawer-items (cart.js)
   - <f365-cart-recs>: "You may also like" carousel with quick add
   Depends on Dawn's global.js, cart.js, cart-drawer.js (already in the theme).
   ========================================================================== */
(() => {
  'use strict';

  /* ------------------------------------------------------------------
     1. Added-state toggle: renderContents() is what runs after an add,
        open() is what the header cart icon calls.
     ------------------------------------------------------------------ */
  customElements.whenDefined('cart-drawer').then(() => {
    const proto = customElements.get('cart-drawer').prototype;
    const origOpen = proto.open;
    const origRender = proto.renderContents;

    proto.open = function (triggeredBy) {
      if (!this.__f365KeepAdded) this.classList.remove('f365-added');
      this.__f365KeepAdded = false;
      return origOpen.call(this, triggeredBy);
    };

    proto.renderContents = function (parsedState) {
      this.classList.add('f365-added');
      this.__f365KeepAdded = true;
      const out = origRender.call(this, parsedState);
      refreshCartPage(); // keep /cart in sync when something is added while on it
      return out;
    };
  });

  /* ------------------------------------------------------------------
     Keep the drawer and the /cart page in sync with each other.
     Dawn only re-renders whichever one did the update.
     ------------------------------------------------------------------ */
  function refreshDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !window.routes) return;
    fetch(`${window.routes.cart_url}?section_id=cart-drawer`)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('#CartDrawer');
        const current = document.getElementById('CartDrawer');
        const freshDrawer = doc.querySelector('cart-drawer');
        if (!fresh || !current) return;
        current.innerHTML = fresh.innerHTML;
        drawer.classList.toggle('is-empty', !!(freshDrawer && freshDrawer.classList.contains('is-empty')));
        drawer.classList.remove('f365-added');
        const overlay = drawer.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
      })
      .catch(() => {});
  }

  function refreshCartPage() {
    const items = document.getElementById('main-cart-items');
    if (!items || !window.routes) return;
    const footer = document.getElementById('main-cart-footer');
    const ids = [items.dataset.id, footer && footer.dataset.id].filter(Boolean);
    fetch(`${window.routes.cart_url}?sections=${ids.join(',')}`)
      .then((r) => r.json())
      .then((sections) => {
        const swap = (host, html) => {
          if (!host || !html) return;
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.js-contents');
          const target = host.querySelector('.js-contents');
          if (fresh && target) target.innerHTML = fresh.innerHTML;
        };
        swap(items, sections[items.dataset.id]);
        if (footer) swap(footer, sections[footer.dataset.id]);
        const hasItems = !!items.querySelector('.cart-item');
        const cartItems = document.querySelector('cart-items');
        if (cartItems) cartItems.classList.toggle('is-empty', !hasItems);
        if (footer) footer.classList.toggle('is-empty', !hasItems);
        const recs = cartItems && cartItems.querySelector('f365-cart-recs');
        if (recs) {
          const variantIds = [...items.querySelectorAll('[data-quantity-variant-id]')].map((i) => i.dataset.quantityVariantId);
          recs.dataset.cartVariants = variantIds.join(',') + ',';
          recs.render();
        }
      })
      .catch(() => {});
  }

  // On /cart, a quantity change or removal (from the page or the drawer) publishes source 'cart-items';
  // refresh both views so they never disagree.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || event.source !== 'cart-items') return;
      if (!document.getElementById('main-cart-items')) return;
      refreshDrawer();
      refreshCartPage();
    });
  }

  /* ------------------------------------------------------------------
     2. Quantity stepper. Buttons carry data-f365-step="1" / "-1".
        We change the input and fire a bubbling "change" so Dawn's
        CartItems.onChange -> validateQuantity -> updateQuantity runs.
     ------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-f365-step]');
    if (!btn || btn.disabled) return;
    if (!btn.closest('cart-drawer, cart-items')) return; // drawer or /cart page
    const input = btn.parentElement.querySelector('input[type="number"]');
    if (!input || input.disabled) return;

    const step = parseInt(btn.dataset.f365Step, 10) || 1;
    const inc = parseInt(input.step, 10) || 1;
    const min = parseInt(input.dataset.min, 10) || 0;
    const max = input.max ? parseInt(input.max, 10) : Infinity;
    let value = (parseInt(input.value, 10) || 0) + step * inc;

    if (value < 0) value = 0;
    if (value > 0 && value < min) value = step > 0 ? min : 0;
    if (value > max) value = max;
    if (value === parseInt(input.value, 10)) return;

    input.value = value;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ------------------------------------------------------------------
     3. Recommendations carousel
     ------------------------------------------------------------------ */
  const recsCache = new Map(); // key -> Promise<product[]>

  function formatMoney(cents, format) {
    const amount = (Number(cents) || 0) / 100;
    const fmt = format || '${{amount}}';
    const fixed = (n, d) => n.toFixed(d);
    const withComma = (s) => s.replace('.', ',');
    const group = (s, sep) => s.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return group(fixed(amount, 0), ',');
        case 'amount_with_comma_separator': {
          const [i, d] = fixed(amount, 2).split('.');
          return group(i, '.') + ',' + d;
        }
        case 'amount_no_decimals_with_comma_separator':
          return group(fixed(amount, 0), '.');
        case 'amount_with_apostrophe_separator': {
          const [i, d] = fixed(amount, 2).split('.');
          return group(i, "'") + '.' + d;
        }
        default: {
          const [i, d] = fixed(amount, 2).split('.');
          return group(i, ',') + '.' + d;
        }
      }
    });
  }

  function fetchJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then((r) => (r.ok ? r.json() : null));
  }

  function isHidden(product) {
    const tags = product.tags || [];
    return tags.some((t) => String(t).toLowerCase() === 'hidden-from-catalog');
  }

  function purchasableVariant(product) {
    const variants = product.variants || [];
    const available = variants.filter((v) => v.available !== false);
    if (!available.length) return { variant: null, kind: 'soldout' };
    if (variants.length === 1) return { variant: variants[0], kind: 'single' };
    return { variant: available[0], kind: 'multi' };
  }

  function loadRecommendations(el) {
    const productId = el.dataset.productId;
    const limit = parseInt(el.dataset.limit, 10) || 6;
    const base = el.dataset.url || '/recommendations/products';
    const fallbacks = (el.dataset.fallbackHandles || '')
      .split(',')
      .map((h) => h.trim())
      .filter(Boolean);
    const key = `${productId}|${fallbacks.join(',')}`;

    if (recsCache.has(key)) return recsCache.get(key);

    const promise = (async () => {
      const ok = (p) => p && p.available !== false && !isHidden(p) && String(p.id) !== String(productId);
      const seen = new Set();
      const push = (list, p) => {
        if (ok(p) && !seen.has(p.handle)) {
          seen.add(p.handle);
          list.push(p);
        }
      };

      // Shopify recommendations: "complementary" (Search & Discovery pairings) first, then "related".
      let complementary = [];
      let related = [];
      if (productId) {
        const [c, r] = await Promise.all([
          fetchJSON(`${base}.json?product_id=${productId}&limit=${limit}&intent=complementary`).catch(() => null),
          fetchJSON(`${base}.json?product_id=${productId}&limit=${limit}&intent=related`).catch(() => null),
        ]);
        complementary = (c && c.products) || [];
        related = (r && r.products) || [];
      }

      // Accessories that pair with anything (the mockup's list); de-duped against the recommendations.
      const fallbackProducts = await Promise.all(
        fallbacks.map((h) => fetchJSON(`/products/${h}.js`).catch(() => null))
      );

      const quickAddable = (p) => purchasableVariant(p).kind === 'single';
      const products = [];
      complementary.forEach((p) => push(products, p));
      // Put quick-addable picks before "Options" cards so the carousel opens on one-tap adds.
      [...fallbackProducts, ...related].filter((p) => p && quickAddable(p)).forEach((p) => push(products, p));
      [...fallbackProducts, ...related].filter((p) => p && !quickAddable(p)).forEach((p) => push(products, p));
      return products.slice(0, limit);
    })();

    recsCache.set(key, promise);
    return promise;
  }

  const CHECK_SVG =
    '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';

  class F365CartRecs extends HTMLElement {
    connectedCallback() {
      this.moneyFormat = this.dataset.moneyFormat || (window.f365MoneyFormat || '${{amount}}');
      this.render();
      this.addEventListener('click', this.onClick.bind(this));
    }

    cartVariantIds() {
      const items = this.closest('cart-drawer-items');
      const raw = this.dataset.cartVariants || (items && items.dataset.cartVariants) || '';
      return new Set(raw.split(',').filter(Boolean));
    }

    async render() {
      const track = this.querySelector('[data-f365-track]');
      if (!track) return;
      let products = [];
      try {
        products = await loadRecommendations(this);
      } catch (e) {
        products = [];
      }
      if (!this.isConnected) return;
      if (!products.length) {
        this.hidden = true;
        return;
      }
      this.hidden = false;
      const inCart = this.cartVariantIds();

      track.innerHTML = products
        .map((p) => {
          const { variant, kind } = purchasableVariant(p);
          const img = p.featured_image || (p.images && p.images[0]) || '';
          const imgUrl = typeof img === 'string' ? img : img && img.src ? img.src : '';
          const sized = imgUrl ? imgUrl.replace(/(\.[a-z]+)(\?|$)/i, '_300x300$1$2') : '';
          const price = formatMoney(variant ? variant.price : p.price, this.moneyFormat);
          const added = variant && inCart.has(String(variant.id));
          let button;
          if (kind === 'soldout') {
            button = `<button type="button" class="f365-recs__btn" disabled>Sold out</button>`;
          } else if (kind === 'multi') {
            button = `<a class="f365-recs__btn" href="${p.url}">Options</a>`;
          } else if (added) {
            button = `<button type="button" class="f365-recs__btn is-added" data-variant-id="${variant.id}" aria-disabled="true">${CHECK_SVG}Added</button>`;
          } else {
            button = `<button type="button" class="f365-recs__btn" data-variant-id="${variant.id}" data-quick-add>Quick Add</button>`;
          }
          const title = String(p.title || '').replace(/</g, '&lt;');
          return `
            <div class="f365-recs__card">
              <a class="f365-recs__img" href="${p.url}" tabindex="-1" aria-hidden="true">
                ${sized ? `<img src="${sized}" alt="" loading="lazy" width="132" height="110">` : ''}
              </a>
              <div class="f365-recs__body">
                <a class="f365-recs__name" href="${p.url}">${title}</a>
                <div class="f365-recs__price">${price}</div>
                ${button}
              </div>
            </div>`;
        })
        .join('');
    }

    onClick(event) {
      const btn = event.target.closest('[data-quick-add]');
      if (!btn || btn.classList.contains('is-loading') || btn.classList.contains('is-added')) return;
      event.preventDefault();
      this.quickAdd(btn);
    }

    async quickAdd(btn) {
      const variantId = btn.dataset.variantId;
      const drawer = this.closest('cart-drawer');
      // On the /cart page the carousel lives inside <cart-items>; refresh that page in place instead.
      const pageItems = !drawer && document.getElementById('main-cart-items');
      const pageFooter = !drawer && document.getElementById('main-cart-footer');
      if (!variantId || (!drawer && !pageItems)) return;

      btn.classList.add('is-loading');
      btn.textContent = 'Adding…';

      const sections = drawer
        ? drawer.getSectionsToRender().map((s) => s.id)
        : [pageItems.dataset.id, pageFooter && pageFooter.dataset.id, 'cart-icon-bubble'].filter(Boolean);
      const body = JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sections.join(','),
        sections_url: window.location.pathname,
      });

      const config = typeof fetchConfig === 'function' ? fetchConfig('json') : {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      };
      config.body = body;

      try {
        const response = await fetch(`${window.routes.cart_add_url}`, config).then((r) => r.json());
        if (response.status) {
          btn.classList.remove('is-loading');
          btn.textContent = response.description || 'Unavailable';
          setTimeout(() => (btn.textContent = 'Quick Add'), 2500);
          return;
        }
        if (drawer) {
          drawer.setActiveElement(document.activeElement);
          drawer.renderContents(response);
          return;
        }
        this.renderCartPage(response, variantId);
      } catch (e) {
        console.error('[365 cart drawer] quick add failed', e);
        btn.classList.remove('is-loading');
        btn.textContent = 'Quick Add';
      }
    }

    renderCartPage(response, variantId) {
      const parse = (html, selector) => {
        const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
        return el ? el.innerHTML : null;
      };
      const swap = (id, selector) => {
        const host = document.getElementById(id);
        if (!host) return;
        const html = response.sections && response.sections[host.dataset.id];
        if (!html) return;
        const target = host.querySelector(selector) || host;
        const inner = parse(html, selector);
        if (inner !== null) target.innerHTML = inner;
        host.classList.remove('is-empty');
      };
      swap('main-cart-items', '.js-contents');
      swap('main-cart-footer', '.js-contents');
      const cartItems = document.querySelector('cart-items');
      if (cartItems) cartItems.classList.remove('is-empty');

      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && response.sections && response.sections['cart-icon-bubble']) {
        const inner = parse(response.sections['cart-icon-bubble'], '.shopify-section');
        if (inner !== null) bubble.innerHTML = inner;
      }

      const ids = this.cartVariantIds();
      ids.add(String(variantId));
      this.dataset.cartVariants = [...ids].join(',') + ',';
      this.render();
      refreshDrawer();
    }
  }

  if (!customElements.get('f365-cart-recs')) customElements.define('f365-cart-recs', F365CartRecs);
})();
